import React from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { LoadingComponent } from "@iventis/components";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { Body2 } from "@iventis/styles";
import { useDelayGetQuery } from "@iventis/api";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { LIST_ITEM_QUERY_KEY } from "@iventis/datafield-editor";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { convertAreaMeasurement, convertLengthMeasurement } from "@iventis/utilities";
import { MapObjectProperties } from "@iventis/map-types";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { AreaModelRotation } from "@iventis/domain-model/model/areaModelRotation";
import { getDataFieldListItem } from "./services/data-field-list-item-service";

const LIST_ITEM_DELAY_MS = 200;

export function MapObjectToolTipValues({
    id,
    name,
    type,
    mapObjectDataFieldValues,
    mapObjectNameDataFieldId,
    mapObjectAreaDataFieldId,
    mapObjectLengthDataFieldId,
    mapObjectRotationZOfModelsInAreaDataFieldId,
    mapObjectIconRotationDataFieldId,
    unitOfMeasurement,
    amountOfValues,
    layer,
}: {
    id: string;
    name: string;
    type: DataFieldType;
    mapObjectDataFieldValues: MapObjectProperties;
    mapObjectNameDataFieldId: string;
    mapObjectAreaDataFieldId: string;
    mapObjectLengthDataFieldId: string;
    mapObjectRotationZOfModelsInAreaDataFieldId: string;
    mapObjectIconRotationDataFieldId: string;
    unitOfMeasurement: UnitOfMeasurement;
    amountOfValues: number;
    layer: MapLayer;
}) {
    const dataFieldValue = mapObjectDataFieldValues[id];

    // Icon rotation can be null, so check first
    if (id === mapObjectIconRotationDataFieldId) {
        return (
            <>
                <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
                <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>
                    {typeof dataFieldValue === "number" || typeof dataFieldValue === "string" ? Number(dataFieldValue) : 0}
                </MapObjectDataFieldValue>
            </>
        );
    }

    if (dataFieldValue == null) {
        return (
            <>
                <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
                <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>-</MapObjectDataFieldValue>
            </>
        );
    }

    if (id === mapObjectNameDataFieldId && typeof dataFieldValue === "string") {
        return (
            <MapObjectDataFieldValue addMargin={amountOfValues > 1} data-testid="map-object-tooltip-value-name">
                {dataFieldValue}
            </MapObjectDataFieldValue>
        );
    }

    if (type === DataFieldType.List && typeof dataFieldValue === "string") {
        return <MapObjectListItemTooltip dataFieldId={id} dataFieldName={name} listItemId={dataFieldValue} />;
    }

    if (id === mapObjectAreaDataFieldId && typeof dataFieldValue === "number") {
        return (
            <>
                <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
                <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>{convertAreaMeasurement(dataFieldValue, unitOfMeasurement)}</MapObjectDataFieldValue>
            </>
        );
    }

    if (id === mapObjectLengthDataFieldId && typeof dataFieldValue === "number") {
        return (
            <>
                <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
                <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>{convertLengthMeasurement(dataFieldValue, unitOfMeasurement)}</MapObjectDataFieldValue>
            </>
        );
    }

    if (id === mapObjectRotationZOfModelsInAreaDataFieldId && layer.areaModelStyle.rotationType.staticValue.staticValue === AreaModelRotation.Random) {
        return (
            <>
                <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
                <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>{translate(Content.map10.areaModel.rotation.random)}</MapObjectDataFieldValue>
            </>
        );
    }

    return (
        <>
            <MapObjectDataFieldName>{name}</MapObjectDataFieldName>
            <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${name}`}>{dataFieldValue as string}</MapObjectDataFieldValue>
        </>
    );
}

function MapObjectListItemTooltip({ dataFieldId, dataFieldName, listItemId }: { dataFieldId: string; listItemId: string; dataFieldName: string }) {
    const theme = useTheme();

    const { isLoading, data } = useDelayGetQuery<DataFieldListItem>([LIST_ITEM_QUERY_KEY, listItemId], () => getDataFieldListItem(dataFieldId, listItemId), LIST_ITEM_DELAY_MS, {
        refetchOnMount: false,
    });

    return (
        <>
            <MapObjectDataFieldName>{dataFieldName}</MapObjectDataFieldName>
            <ListItemLoadingContainer>
                {isLoading ? (
                    <LoadingComponent sx={{ color: theme.typographyColors.blank }} size={20} />
                ) : (
                    <MapObjectDataFieldValue data-testid={`map-object-tooltip-value-${dataFieldName}`}>{data?.name}</MapObjectDataFieldValue>
                )}
            </ListItemLoadingContainer>
        </>
    );
}

const ListItemLoadingContainer = styled.div`
    width: fit-content;
`;

const MapObjectDataFieldValue = styled(Body2)<{ addMargin?: boolean }>`
    font-weight: 700;
    margin-bottom: ${({ addMargin }) => (addMargin ? "10px" : "0px")};
    word-wrap: break-word;
`;

const MapObjectDataFieldName = styled(Body2)`
    font-weight: 400;
    word-wrap: break-word;
`;
